import React, { useEffect, useRef } from 'react';

const ShaderBackground = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const fragmentShaderSource = `#version 300 es
precision highp float;
out vec4 O;
uniform vec2 resolution;
uniform float time;

#define FC gl_FragCoord.xy
#define T time
#define R resolution
#define MN min(R.x,R.y)

void main(void) {
  vec2 uv=(FC-.5*R)/MN;
  vec3 col=vec3(0);
  
  // Create pulsing effect for stars
  uv*=1.-.3*(sin(T*.2)*.5+.5);
  
  // Generate moving stars with trails
  for (float i=1.; i<12.; i++) {
    // Create movement pattern for each star layer
    uv+=.1*cos(i*vec2(.1+.01*i, .8)+i*i+T*.5+.1*uv.x);
    vec2 p=uv;
    float d=length(p);
    
    // Create star with colored glow and trail effect
    col+=.00125/d*(cos(sin(i)*vec3(1,2,3))+1.);
    
    // Add subtle trail effect without cloud noise
    float trail = 1.0/length(max(p,vec2(p.x*.02,p.y)));
    col+=.001*trail*(cos(sin(i)*vec3(1.5,2.5,3.5))+1.);
  }
  
  O=vec4(col,1);
}`;

  const vertexShaderSource = `#version 300 es
precision highp float;
in vec4 position;
void main(){
  gl_Position=position;
}`;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl2');
    if (!gl) {
      console.error('WebGL 2 not supported');
      return;
    }

    // Resize canvas
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      
      // Set the canvas size to match its display size
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      
      // Set the canvas display size
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    // Initial resize
    resize();

    // Create resize observer for container size changes
    const resizeObserver = new ResizeObserver(() => {
      resize();
    });

    // Observe the canvas element
    resizeObserver.observe(canvas);

    // Handle window resize
    window.addEventListener('resize', resize);

    // Compile shader
    const compileShader = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compilation error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    // Create program
    const createProgram = (vertexShader, fragmentShader) => {
      const program = gl.createProgram();
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);
      
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error('Program linking error:', gl.getProgramInfoLog(program));
        gl.deleteProgram(program);
        return null;
      }
      return program;
    };

    // Setup shaders and program
    const vertexShader = compileShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = compileShader(gl.FRAGMENT_SHADER, fragmentShaderSource);
    
    if (!vertexShader || !fragmentShader) return;
    
    const program = createProgram(vertexShader, fragmentShader);
    if (!program) return;

    // Setup geometry (full screen quad)
    const vertices = new Float32Array([-1, 1, -1, -1, 1, 1, 1, -1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

    const position = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    // Get uniform locations
    const resolutionLocation = gl.getUniformLocation(program, 'resolution');
    const timeLocation = gl.getUniformLocation(program, 'time');

    // Render function
    const render = (time) => {
      gl.clearColor(0, 0, 0, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      
      gl.useProgram(program);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
      gl.uniform1f(timeLocation, time * 0.001);
      
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      
      animationRef.current = requestAnimationFrame(render);
    };

    // Start animation
    animationRef.current = requestAnimationFrame(render);

    // Cleanup
    return () => {
      window.removeEventListener('resize', resize);
      resizeObserver.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      gl.deleteBuffer(buffer);
    };
  }, [fragmentShaderSource, vertexShaderSource]);

  return (
    <canvas 
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full object-cover ${className}`}
      style={{ 
        touchAction: 'none',
        zIndex: 1,
        opacity: 0.5,
        width: '100%',
        height: '100%'
      }}
    />
  );
};

export default ShaderBackground;